"use client";

import { createContext, useContext, useState, useId, useCallback } from "react";
import type { HeaderContextProps } from "./types/header.types";
import { useKeypress } from "../../hooks";

// eslint-disable-next-line react-refresh/only-export-components -- exports context alongside provider
export const HeaderContext = createContext<HeaderContextProps>({
  menuId: "",
  menuOpen: false,
  setMenuOpen: () => {},
  toggleMenu: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components -- exports context alongside provider
export const useHeaderContext = () => useContext(HeaderContext);

interface HeaderProviderProps {
  children: React.ReactNode;
  onMenuOpenChange?: (open: boolean) => void;
}

export const HeaderProvider: React.FC<HeaderProviderProps> = ({ children, onMenuOpenChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const menuId = useId();

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => {
      const next = !prev;
      onMenuOpenChange?.(next);
      return next;
    });
  }, [onMenuOpenChange]);

  const handleSetMenuOpen = useCallback(
    (open: boolean) => {
      onMenuOpenChange?.(open);
      setMenuOpen(open);
    },
    [onMenuOpenChange],
  );

  useKeypress("Escape", () => {
    handleSetMenuOpen(false);
  });

  return (
    <HeaderContext.Provider value={{ menuId, menuOpen, setMenuOpen: handleSetMenuOpen, toggleMenu }}>
      {children}
    </HeaderContext.Provider>
  );
};
